<div
  class="tw:flex tw:flex-col tw:justify-between tw:relative tw:w-full tw:overflow-visible tw:scroll-smooth tw:h-full">
  <!-- New Interaction Template -->
  @if (dedupedInteractions()?.length === 0 && writeAccess()) {
    <ng-container *ngTemplateOutlet="quickStartTemplate()" />
  } @else {
    <div class="tw:flex tw:p-6 tw:flex-col-reverse">
      <ng-container *ngTemplateOutlet="contentTemplate()" />
      <!-- New message loading -->
      @if (newMessageLoading() === CHAT_ROLE.ASSISTANT) {
        <div class="tw:flex tw:gap-3 tw:w-fit tw:max-w-[80%] tw:mb-auto">
          <p-avatar
            class="tw:shrink-0"
            shape="circle">
            <i class="coricon-other-agent tw:text-xl"></i>
          </p-avatar>
          <div class="tw:flex tw:flex-col tw:gap-2">
            <strong>
              Assistant
              <small class="tw:font-normal tw:text-muted-color tw:px-1">
                Generating Response...
              </small>
            </strong>

            <div class="tw:whitespace-pre-wrap tw:wrap-anywhere tw:flex tw:flex-col tw:gap-1">
              <cor-skeleton-loader
                width="18rem"
                height="0.8rem" />
              <cor-skeleton-loader
                width="14rem"
                height="0.8rem" />
            </div>
          </div>
        </div>
      } @else if (newMessageLoading() === CHAT_ROLE.USER) {
        <div
          class="tw:flex tw:gap-3 tw:w-fit tw:max-w-[70%] tw:mb-auto tw:mr-2 tw:items-end tw:self-end tw:pb-2">
          <div
            class="tw:flex tw:flex-col tw:gap-2 tw:bg-emphasis tw:rounded-xl tw:border tw:border-surface tw:p-3">
            <strong>
              {{ testRun()?.personName || 'User' }}
              <small class="tw:font-normal tw:text-muted-color tw:px-1">
                Generating Response...
              </small>
            </strong>

            <div class="tw:whitespace-pre-wrap tw:wrap-anywhere tw:flex tw:flex-col tw:gap-1">
              <cor-skeleton-loader
                width="18rem"
                height="0.8rem" />
              <cor-skeleton-loader
                width="14rem"
                height="0.8rem" />
            </div>
          </div>
          <p-avatar
            class="tw:shrink-0"
            [ngStyle]="{
              backgroundColor: testRun()?.personName || 'User' | uniqueRgbColor,
              color: 'white',
            }"
            [label]="testRun()?.personName || 'User' | nameBadge"
            [pTooltip]="testRun()?.personDescription"
            tooltipPosition="left"
            shape="circle" />
        </div>
      }
      <ng-template
        #interactionContentTemplate
        let-content="content"
        let-contentType="contentType"
        let-index="index">
        <div class="tw:flex tw:flex-wrap tw:gap-2">
          @for (part of content; track $index) {
            @if (part.type === 'text') {
              <div class="tw:basis-full">
                @if (contentType === CONTENT_MIME_TYPE.MARKDOWN) {
                  <div [ngClass]="'scope-chat-message-' + index">
                    <markdown
                      class="tw:prose tw:dark:prose-invert tw:max-w-none tw:wrap-anywhere"
                      [data]="part.text | scopeMarkdownStyles: 'scope-chat-message-' + index" />
                  </div>
                } @else {
                  <div
                    class="tw:whitespace-pre-wrap tw:wrap-anywhere"
                    [innerHTML]="part.text | linkify"></div>
                }
              </div>
            } @else if (part.type === 'image_file') {
              <div
                class="tw:relative tw:h-20 tw:w-20 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-surface tw:bg-emphasis">
                @let imageUrl = attachmentService.getDownloadUrl(part.image_file.file_id);
                <img
                  class="tw:h-full tw:w-full tw:cursor-zoom-in tw:object-cover"
                  [src]="imageUrl"
                  alt="Attached image"
                  (click)="imagePreview.open(imageUrl)" />
              </div>
            } @else if (part.type === 'image_url') {
              <div
                class="tw:relative tw:h-20 tw:w-20 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-surface tw:bg-emphasis">
                <img
                  class="tw:h-full tw:w-full tw:cursor-zoom-in tw:object-cover"
                  [src]="part.image_url.url"
                  alt="Attached image"
                  (click)="imagePreview.open(part.image_url.url)" />
              </div>
            }
          }
        </div>
      </ng-template>
      @if (!newMessageLoading() && writeAccess() && testSessionId() && !testRun()?.hasEnded) {
        <div
          class="tw:flex tw:gap-1 tw:items-center tw:py-4 tw:justify-center tw:text-muted-color"
          test-id="chat-end-message">
          Are you done with this chat session?
          <p-button
            [buttonProps]="{ autofocus: false }"
            label="End Session"
            variant="text"
            (click)="reviewSession.emit()"
            test-id="end-session-button" />
        </div>
      }
      <!-- Use .slice() to create a copy of the interactions which is reversed
       and we are mapping last=$first because we are using flex direction as column-reverse So, the items
       here are being rendered from the bottom to the top -->
      @for (
        testInteraction of dedupedInteractions().slice().reverse();
        track testInteraction.id;
        let last = $first;
        let index = $index
      ) {
        <!-- Context -->
        @if (
          testInteraction.context && interactionState().get(testInteraction.id)?.isContextExpanded
        ) {
          <p-message
            severity="secondary"
            class="tw:my-2"
            [ngClass]="{
              'tw:mb-auto': last && !newMessageLoading(),
            }">
            <ng-template pTemplate="container">
              <div class="tw:flex tw:flex-col tw:gap-2 tw:w-full">
                <div class="tw:flex tw:items-center tw:gap-2">
                  <i class="pi pi-file-o tw:color-inherit"></i>
                  <div class="tw:font-medium tw:grow">Context</div>
                  <p-button
                    test-id="chat-update-interaction-state"
                    [buttonProps]="{ autofocus: false }"
                    size="small"
                    variant="text"
                    severity="secondary"
                    icon="pi pi-times"
                    rounded
                    (click)="
                      updateInteractionState(testInteraction.id, { isContextExpanded: false })
                    " />
                </div>
                <div class="tw:bg-(--p-content-background) tw:rounded-xl">
                  <cor-code-block
                    scrollHeight="20rem"
                    [code]="testInteraction.context | cellPrettify"
                    [objectLinks]="[]"
                    [allowCopy]="true" />
                </div>
              </div>
            </ng-template>
          </p-message>
        }
        <!-- Logs -->
        @if (
          testInteraction.executionLogs &&
          interactionState().get(testInteraction.id)?.isLogsExpanded
        ) {
          <p-message
            severity="secondary"
            class="tw:my-1"
            [ngClass]="{
              'tw:mb-auto': last && !newMessageLoading(),
            }">
            <ng-template pTemplate="container">
              <div class="tw:flex tw:flex-col tw:gap-2 tw:w-full">
                <div class="tw:flex tw:items-center tw:gap-2">
                  <i class="pi pi-code tw:color-inherit"></i>
                  <div class="tw:font-medium tw:grow">Logs</div>
                  <p-button
                    test-id="chat-update-interaction-state-2"
                    [buttonProps]="{ autofocus: false }"
                    size="small"
                    variant="text"
                    severity="secondary"
                    icon="pi pi-times"
                    rounded
                    (click)="
                      updateInteractionState(testInteraction.id, { isLogsExpanded: false })
                    " />
                </div>
                <div class="tw:bg-(--p-content-background) tw:rounded-xl">
                  <cor-code-block
                    scrollHeight="20rem"
                    [code]="testInteraction.executionLogs"
                    language="logs"
                    [objectLinks]="[]"
                    [allowCopy]="true" />
                </div>
              </div>
            </ng-template>
          </p-message>
        }
        <!-- Display Error Message and its visible on toggle of error icon in user message. -->
        @if (
          testInteraction.errorMessage &&
          interactionState().get(testInteraction.id)?.isErrorExpanded
        ) {
          <p-message
            severity="error"
            class="tw:my-1"
            [ngClass]="{
              'tw:mb-auto': last && !newMessageLoading(),
            }">
            <ng-template pTemplate="container">
              <div class="tw:flex tw:flex-col tw:gap-2 tw:w-full">
                <div class="tw:flex tw:items-center tw:gap-2">
                  <i class="pi pi-exclamation-triangle tw:color-inherit"></i>
                  <div class="tw:text-lg tw:font-medium tw:grow">Error Logs</div>
                  <p-button
                    test-id="chat-update-interaction-state-3"
                    [buttonProps]="{ autofocus: false }"
                    size="small"
                    variant="text"
                    severity="danger"
                    icon="pi pi-times"
                    rounded
                    (click)="
                      updateInteractionState(testInteraction.id, { isErrorExpanded: false })
                    " />
                </div>
                <div class="tw:bg-(--p-content-background) tw:rounded-xl">
                  <cor-code-block
                    scrollHeight="20rem"
                    language="logs"
                    [objectLinks]="pipelineAssets()"
                    [code]="testInteraction.errorMessage.trim()"
                    [copyWithLink]="true"
                    [allowDownload]="true"
                    (downloadEvent)="downloadTxtFile(testInteraction.errorMessage)" />
                </div>
              </div>
            </ng-template>
          </p-message>
        }

        <!-- Assistant with Content-->
        @if (testInteraction.role === CHAT_ROLE.ASSISTANT) {
          <!-- Toolcalls (this will be shown below the chat as we use col-reverse) -->
          @if (testInteraction.tool_calls?.length > 0) {
            <div
              class="tw:group tw:flex tw:items-center tw:gap-3 tw:w-fit tw:max-w-[80%] tw:border-l tw:border-surface tw:p-1 tw:ps-4 tw:ms-[5rem]"
              [ngClass]="{
                'tw:mb-auto': last && !newMessageLoading(),
              }">
              <small class="tw:text-muted-color tw:text-xs tw:shrink-0">
                {{ testInteraction.createdDate | messageTime }}
              </small>
              <div class="tw:flex tw:flex-col tw:gap-2">
                <!-- Tool calls -->
                @if (testInteraction.tool_calls?.length > 0) {
                  <div class="tw:flex tw:flex-wrap tw:gap-2">
                    <!-- Tool Call -->
                    @for (tool of testInteraction.tool_calls; track tool.id) {
                      <p-tag
                        class="tw:gap-2 tw:px-2 tw:items-center tw:font-semibold tw:cursor-pointer"
                        [ngStyle]="{
                          '--p-tag-icon-size': '0.9rem',
                        }"
                        severity="info"
                        icon="pi pi-wrench"
                        [value]="tool.function.name"
                        (click)="
                          toolCallPopover.showPopover(tool, $event, toolCallPopoverElement)
                        " />
                    }
                  </div>
                }
              </div>
            </div>
          }
          <!-- Content -->
          @if (testInteraction.content) {
            <div
              [id]="'message-' + testInteraction.id"
              class="tw:group tw:flex tw:gap-3 tw:w-fit tw:max-w-[80%] tw:my-2"
              [ngClass]="{
                'tw:mb-auto': last && !newMessageLoading(),
                'tw:border tw:border-primary tw:rounded':
                  testInteraction.id && highlightedMessageId() === testInteraction.id,
              }">
              <p-avatar
                class="tw:shrink-0"
                shape="circle">
                <i class="coricon-other-agent tw:text-xl"></i>
              </p-avatar>

              <div class="tw:flex tw:flex-col tw:gap-2">
                <span>
                  <strong [pTooltip]="testInteraction.nameTooltip">
                    {{ testInteraction.name }}
                  </strong>
                  <small class="tw:font-normal tw:text-muted-color tw:px-1">
                    {{ testInteraction.createdDate | messageTime }}
                    {{ testInteraction.timeTaken | timeTaken }}
                  </small>
                </span>
                <ng-container
                  *ngTemplateOutlet="
                    interactionContentTemplate;
                    context: {
                      content: testInteraction.content,
                      contentType: testInteraction.contentType,
                      index: testInteraction.id || index,
                    }
                  " />

                <!-- Start: Debug tag list -->
                @if (debugMode() && testInteraction.debugTags?.length) {
                  <div class="tw:flex tw:flex-wrap tw:gap-1 tw:mt-2">
                    @for (tag of testInteraction.debugTags; let index = $index; track tag + index) {
                      <p-tag
                        class="tw:bg-purple-500/10 tw:text-purple-500"
                        severity="warn"
                        [value]="tag" />
                    }
                  </div>
                }
                <!-- End: Debug tag list -->

                <!-- Start: Interaction Buttons -->
                @if (
                  last &&
                  writeAccess() &&
                  !testRun()?.hasEnded &&
                  !newMessageLoading() &&
                  testInteraction.buttons?.length
                ) {
                  <div class="tw:flex tw:flex-wrap tw:gap-2 tw:mt-2">
                    @for (
                      quickBtn of testInteraction.buttons;
                      let index = $index;
                      track quickBtn.label + index
                    ) {
                      <p-button
                        test-id="chat-send-quick-button-message"
                        [buttonProps]="{ autofocus: false }"
                        severity="secondary"
                        [pTooltip]="quickBtn.tooltip"
                        tooltipPosition="bottom"
                        (click)="sendQuickButtonMessage(quickBtn.userMessage)"
                        [label]="quickBtn.label" />
                    }
                  </div>
                }
                <!-- End: Interaction buttons -->

                <!-- Action Buttons and comments -->
                <div
                  test-id="transcript-rating"
                  class="tw:flex tw:w-fit tw:items-center tw:gap-2 tw:p-2 tw:bg-emphasis tw:rounded-lg"
                  [ngClass]="{
                    'tw:invisible tw:group-hover:visible': !writeAccess() || !last,
                    'tw:visible': [1, -1] | includes: testInteraction.rating,
                  }">
                  <!-- Like -->
                  @if (writeAccess() || testInteraction.rating === FEEDBACK_TYPE.LIKE) {
                    @if (interactionState().get(testInteraction.id)?.isLikeSaving) {
                      <i class="pi pi-spin pi-spinner"></i>
                    } @else {
                      <i
                        class="pi"
                        test-id="transcript-rate-accurate"
                        [attr.data-selected]="testInteraction.rating === FEEDBACK_TYPE.LIKE"
                        [ngClass]="{
                          'pi-thumbs-up-fill tw:text-primary':
                            testInteraction.rating === FEEDBACK_TYPE.LIKE,
                          'pi-thumbs-up tw:text-color':
                            testInteraction.rating !== FEEDBACK_TYPE.LIKE,
                          'tw:cursor-pointer': writeAccess(),
                          disabled: !writeAccess(),
                        }"
                        [pTooltip]="writeAccess() && 'Valid Response'"
                        (click)="
                          writeAccess() &&
                            postRatingFeedback(
                              testInteraction.id,
                              FEEDBACK_TYPE.LIKE,
                              testInteraction.rating === FEEDBACK_TYPE.LIKE
                                ? FEEDBACK_TYPE.NONE
                                : FEEDBACK_TYPE.LIKE
                            )
                        "></i>
                    }
                  }

                  <!-- Dislike -->
                  @if (writeAccess() || testInteraction.rating === FEEDBACK_TYPE.DISLIKE) {
                    @if (interactionState().get(testInteraction.id)?.isDislikeSaving) {
                      <i class="pi pi-spin pi-spinner"></i>
                    } @else {
                      <i
                        class="pi"
                        test-id="transcript-rate-inaccurate"
                        [attr.data-selected]="testInteraction.rating === FEEDBACK_TYPE.DISLIKE"
                        [ngClass]="{
                          'pi-thumbs-down-fill icon-danger':
                            testInteraction.rating === FEEDBACK_TYPE.DISLIKE,
                          'pi-thumbs-down tw:text-color':
                            testInteraction.rating !== FEEDBACK_TYPE.DISLIKE,
                          'tw:cursor-pointer': writeAccess(),
                          disabled: !writeAccess(),
                        }"
                        [pTooltip]="writeAccess() && 'Incorrect Response'"
                        (click)="
                          writeAccess() &&
                            postRatingFeedback(
                              testInteraction.id,
                              FEEDBACK_TYPE.DISLIKE,
                              testInteraction.rating === FEEDBACK_TYPE.DISLIKE
                                ? FEEDBACK_TYPE.NONE
                                : FEEDBACK_TYPE.DISLIKE
                            )
                        "></i>
                    }
                  }

                  <!-- Copy Message -->
                  @if (
                    copiedInfo().index === index &&
                    copiedInfo().checked &&
                    copiedInfo().type === 'message'
                  ) {
                    <i
                      role="button"
                      class="pi pi-check icon-success tw:cursor-pointer"
                      pTooltip="Copied"></i>
                  } @else {
                    <i
                      test-id="chat-copy-message"
                      class="pi pi-clone tw:text-color tw:cursor-pointer"
                      (click)="copyCode(testInteraction.content, index)"
                      pTooltip="Copy Message"></i>
                  }
                  @if ((testInteraction.feedbackHistory | countFeedbackComment) > 0) {
                    <p-tag
                      class="tw:cursor-pointer"
                      [ngStyle]="{
                        '--p-tag-info-color': interactionState().get(testInteraction.id)
                          ?.visibleActivityCount
                          ? 'var(--p-tag-primary-color)'
                          : 'var(--p-tag-secondary-color)',
                        '--p-tag-icon-size': '1.2rem',
                        '--p-tag-padding': 0,
                        '--p-tag-info-background': 'transparent',
                      }"
                      icon="pi pi-comment"
                      severity="info"
                      [value]="
                        testInteraction.feedbackHistory | countFeedbackComment | number: '1.0-0'
                      "
                      pTooltip="View Comments"
                      (click)="
                        updateInteractionState(testInteraction.id, {
                          visibleActivityCount: interactionState().get(testInteraction.id)
                            ?.visibleActivityCount
                            ? 0
                            : 5,
                        })
                      " />
                  }
                  <i
                    test-id="chat-more-actions"
                    class="pi pi-ellipsis-v tw:text-color tw:cursor-pointer"
                    pTooltip="More Actions"
                    tooltipPosition="bottom"
                    (click)="userMoreOptionsPopover.toggle($event)"></i>

                  <!-- Feedback Contributors Avatars -->
                  @if ((testInteraction.feedbackHistory | uniqueFeedbackContributors).length > 0) {
                    <p-avatar-group
                      (click)="
                        updateInteractionState(testInteraction.id, {
                          visibleActivityCount: interactionState().get(testInteraction.id)
                            ?.visibleActivityCount
                            ? 0
                            : 5,
                        })
                      "
                      class="tw:cursor-pointer">
                      @for (
                        contributor of (
                          testInteraction.feedbackHistory | uniqueFeedbackContributors
                        ).slice(0, 5);
                        track contributor.name
                      ) {
                        <p-avatar
                          class="tw:shrink-0"
                          [label]="contributor.name | nameBadge"
                          [ngStyle]="{
                            '--p-avatar-width': '1.4rem',
                            '--p-avatar-height': '1.4rem',
                            '--p-avatar-font-size': '0.7rem',
                            backgroundColor: (contributor.name | uniqueRgbColor),
                            color: 'white',
                            border: 'none',
                          }"
                          shape="circle"
                          [pTooltip]="contributor.name" />
                      }
                      @if (
                        (testInteraction.feedbackHistory | uniqueFeedbackContributors).length > 5
                      ) {
                        <p-avatar
                          class="tw:shrink-0 tw:font-semibold"
                          [ngStyle]="{
                            '--p-avatar-width': '1.4rem',
                            '--p-avatar-height': '1.4rem',
                            '--p-avatar-font-size': '0.7rem',
                          }"
                          pTooltip="Click to view all contributors"
                          [label]="`+ ${
                            (testInteraction.feedbackHistory | uniqueFeedbackContributors).length -
                            5
                          }`"
                          shape="circle" />
                      }
                    </p-avatar-group>
                  }
                </div>

                @if (debugMode()) {
                  <div
                    class="tw:flex tw:flex-wrap tw:gap-2"
                    [ngClass]="{
                      'tw:invisible tw:group-hover:visible': !last,
                    }">
                    @if (testInteraction.context) {
                      <p-tag
                        class="tw:gap-2 tw:px-2 tw:items-center tw:font-semibold tw:cursor-pointer tw:bg-purple-500/10 tw:text-purple-500"
                        [ngStyle]="{
                          '--p-tag-icon-size': '0.9rem',
                        }"
                        severity="secondary"
                        icon="pi pi-file-o"
                        value="Context"
                        (click)="
                          updateInteractionState(testInteraction.id, {
                            isContextExpanded: !interactionState().get(testInteraction.id)
                              ?.isContextExpanded,
                          })
                        " />
                    }
                    @if (testInteraction.executionLogs) {
                      <p-tag
                        class="tw:gap-2 tw:px-2 tw:items-center tw:font-semibold tw:cursor-pointer"
                        [ngStyle]="{
                          '--p-tag-icon-size': '0.9rem',
                        }"
                        severity="warn"
                        icon="pi pi-code"
                        value="Logs"
                        (click)="
                          updateInteractionState(testInteraction.id, {
                            isLogsExpanded: !interactionState().get(testInteraction.id)
                              ?.isLogsExpanded,
                          })
                        " />
                    }
                  </div>
                }

                <ng-container
                  *ngTemplateOutlet="
                    interactionCommentSectionTemplate;
                    context: { interaction: testInteraction }
                  " />
                <ng-container
                  *ngTemplateOutlet="
                    interactionActivityTemplate;
                    context: { interaction: testInteraction }
                  " />

                <p-popover #userMoreOptionsPopover>
                  <div class="tw:flex tw:flex-col">
                    <!-- Copy Link Icon -->
                    @if (testRun()?.id) {
                      <p-button
                        test-id="chat-copy-link"
                        [buttonProps]="{ autofocus: false }"
                        pTooltip="Copy link to this message"
                        tooltipPosition="bottom"
                        (click)="copyMessageLink(testInteraction.id, $event)"
                        styleClass="tw:justify-start"
                        severity="contrast"
                        size="small"
                        variant="text"
                        fluid
                        [icon]="
                          copiedInfo().index === testInteraction.id &&
                          copiedInfo().checked &&
                          copiedInfo().type === 'link'
                            ? 'pi pi-check icon-success'
                            : 'pi pi-link tw:text-color'
                        "
                        label="Copy Link" />
                    }

                    <ng-container
                      *ngTemplateOutlet="
                        interactionFeedbackActionsTemplate;
                        context: {
                          interaction: testInteraction,
                          popover: userMoreOptionsPopover,
                          showCommentAction: writeAccess(),
                        }
                      " />
                  </div>
                </p-popover>
              </div>
            </div>
          }
        }

        <!-- User -->
        @if (testInteraction.role === CHAT_ROLE.USER) {
          <div
            [id]="'message-' + testInteraction.id"
            class="tw:relative tw:group tw:flex tw:flex-col tw:gap-3 tw:w-fit tw:max-w-[70%] tw:self-end tw:my-2"
            [ngClass]="{
              'tw:mb-auto': last && !newMessageLoading(),
              'tw:border tw:border-primary tw:rounded':
                testInteraction.id && highlightedMessageId() === testInteraction.id,
            }">
            <div class="tw:flex tw:gap-2 tw:items-end tw:self-end">
              @if (testInteraction.errorMessage) {
                <i
                  test-id="chat-an-error-occured-click-to-view-full-erro"
                  class="pi pi-exclamation-circle icon-danger tw:mb-1 tw:text-lg tw:cursor-pointer"
                  pTooltip="An Error Occured - Click to view full error message"
                  (click)="
                    updateInteractionState(testInteraction.id, {
                      isErrorExpanded: !interactionState().get(testInteraction.id)?.isErrorExpanded,
                    })
                  "></i>
              }

              <div
                class="tw:relative tw:flex tw:flex-col tw:gap-2 tw:bg-emphasis tw:rounded-xl tw:border tw:border-surface tw:p-3"
                [ngClass]="{
                  'tw:border-red-300!': testInteraction.errorMessage,
                }">
                <span>
                  <strong
                    [pTooltip]="testInteraction.nameTooltip"
                    tooltipPosition="left">
                    {{ testInteraction.name }}
                  </strong>
                  @if (
                    testInteraction.name !== testInteraction.createdBy && testInteraction.createdBy
                  ) {
                    <small class="tw:font-normal tw:text-muted-color">
                      as {{ testInteraction.createdBy }}
                    </small>
                  }
                  <small
                    class="tw:font-normal tw:text-muted-color tw:px-1"
                    [ngClass]="{
                      'tw:me-4': testInteraction.isAiGenerated,
                    }">
                    {{ testInteraction.createdDate | messageTime }}
                    {{ testInteraction.timeTaken | timeTaken }}
                  </small>
                  @if (testInteraction.isAiGenerated) {
                    <i
                      class="pi pi-sparkles tw:text-muted-color tw:absolute tw:right-3"
                      pTooltip="This message was generated by AI"
                      tooltipPosition="left"></i>
                  }
                </span>
                <ng-container
                  *ngTemplateOutlet="
                    interactionContentTemplate;
                    context: {
                      content: testInteraction.content,
                      contentType: testInteraction.contentType,
                      index: testInteraction.id || index,
                    }
                  " />
              </div>

              <p-avatar
                class="tw:shrink-0"
                [ngStyle]="{
                  backgroundColor: testInteraction.name || '' | uniqueRgbColor,
                  color: 'white',
                }"
                [pTooltip]="testInteraction.nameTooltip"
                tooltipPosition="left"
                [label]="testInteraction.name || '' | nameBadge"
                shape="circle" />
            </div>
            <!-- retry / copy / like/dislike / options icons -->
            <div
              test-id="user-msg-actions"
              class="tw:flex tw:gap-2 tw:items-center tw:ms-auto tw:pe-10"
              [ngClass]="{
                'tw:invisible tw:group-hover:visible': !writeAccess() || !last,
                'tw:visible': [1, -1] | includes: testInteraction.rating,
              }">
              <!-- Start: Rating for AI-genrated user message -->
              @if (testInteraction.isAiGenerated && testInteraction.id) {
                <div class="tw:flex tw:gap-2 tw:items-center">
                  <!-- Like -->
                  <!-- @if (writeAccess() || testInteraction.rating === FEEDBACK_TYPE.LIKE) {
                    @if (interactionState().get(testInteraction.id)?.isLikeSaving) {
                      <i class="pi pi-spin pi-spinner"></i>
                    } @else {
                      <i
                        class="pi"
                        [ngClass]="{
                          'pi-thumbs-up-fill tw:text-primary':
                            testInteraction.rating === FEEDBACK_TYPE.LIKE,
                          'pi-thumbs-up tw:text-color':
                            testInteraction.rating !== FEEDBACK_TYPE.LIKE,
                          'tw:cursor-pointer': writeAccess(),
                          disabled: !writeAccess(),
                        }"
                        [pTooltip]="writeAccess() && ('Valid Response' )"
                        (click)="
                          writeAccess() &&
                            postRatingFeedback(
                              testInteraction.id,
                              FEEDBACK_TYPE.LIKE,
                              testInteraction.rating === FEEDBACK_TYPE.LIKE
                                ? FEEDBACK_TYPE.NONE
                                : FEEDBACK_TYPE.LIKE
                            )
                        "></i>
                    }
                  } -->

                  <!-- Dislike -->
                  @if (writeAccess() || testInteraction.rating === FEEDBACK_TYPE.DISLIKE) {
                    @if (interactionState().get(testInteraction.id)?.isDislikeSaving) {
                      <i class="pi pi-spin pi-spinner"></i>
                    } @else {
                      <i
                        class="pi"
                        test-id="transcript-rate-inaccurate"
                        [attr.data-selected]="testInteraction.rating === FEEDBACK_TYPE.DISLIKE"
                        [ngClass]="{
                          'pi-thumbs-down-fill icon-danger':
                            testInteraction.rating === FEEDBACK_TYPE.DISLIKE,
                          'pi-thumbs-down tw:text-color':
                            testInteraction.rating !== FEEDBACK_TYPE.DISLIKE,
                          'tw:cursor-pointer': writeAccess(),
                          disabled: !writeAccess(),
                        }"
                        [pTooltip]="writeAccess() && 'Incorrect Response'"
                        (click)="
                          writeAccess() &&
                            postRatingFeedback(
                              testInteraction.id,
                              FEEDBACK_TYPE.DISLIKE,
                              testInteraction.rating === FEEDBACK_TYPE.DISLIKE
                                ? FEEDBACK_TYPE.NONE
                                : FEEDBACK_TYPE.DISLIKE
                            )
                        "></i>
                    }
                  }

                  @if ((testInteraction.feedbackHistory | countFeedbackComment) > 0) {
                    <p-tag
                      class="tw:cursor-pointer"
                      [ngStyle]="{
                        '--p-tag-info-color': interactionState().get(testInteraction.id)
                          ?.visibleActivityCount
                          ? 'var(--p-tag-primary-color)'
                          : 'var(--p-tag-secondary-color)',
                        '--p-tag-icon-size': '1.2rem',
                        '--p-tag-padding': 0,
                        '--p-tag-info-background': 'transparent',
                      }"
                      icon="pi pi-comment"
                      severity="info"
                      [value]="
                        testInteraction.feedbackHistory | countFeedbackComment | number: '1.0-0'
                      "
                      pTooltip="View Comments"
                      (click)="
                        updateInteractionState(testInteraction.id, {
                          visibleActivityCount: interactionState().get(testInteraction.id)
                            ?.visibleActivityCount
                            ? 0
                            : 5,
                        })
                      " />
                  }
                </div>
              }
              <!-- End: Rating for AI-generated user message -->
              @if (debugMode()) {
                <div class="tw:flex tw:flex-wrap tw:gap-2">
                  @if (testInteraction.context) {
                    <p-tag
                      class="tw:gap-2 tw:px-2 tw:items-center tw:font-semibold tw:cursor-pointer tw:bg-purple-500/10 tw:text-purple-500"
                      [ngStyle]="{
                        '--p-tag-icon-size': '0.9rem',
                      }"
                      severity="secondary"
                      icon="pi pi-file-o"
                      value="Context"
                      (click)="
                        updateInteractionState(testInteraction.id, {
                          isContextExpanded: !interactionState().get(testInteraction.id)
                            ?.isContextExpanded,
                        })
                      " />
                  }
                  @if (testInteraction.executionLogs) {
                    <p-tag
                      class="tw:gap-2 tw:px-2 tw:items-center tw:font-semibold tw:cursor-pointer"
                      [ngStyle]="{
                        '--p-tag-icon-size': '0.9rem',
                      }"
                      severity="warn"
                      icon="pi pi-code"
                      value="Logs"
                      (click)="
                        updateInteractionState(testInteraction.id, {
                          isLogsExpanded: !interactionState().get(testInteraction.id)
                            ?.isLogsExpanded,
                        })
                      " />
                  }
                </div>
              }
              <!-- Retry Icon -->
              @if (writeAccess() && !testRun()?.hasEnded) {
                <i
                  test-id="chat-retry"
                  class="pi pi-sync tw:text-color tw:cursor-pointer"
                  pTooltip="Retry"
                  (click)="sendPromptEvent.emit(testInteraction.content)"></i>
              }

              <!-- Copy Icon -->
              @if (
                copiedInfo().index === index &&
                copiedInfo().checked &&
                copiedInfo().type === 'message'
              ) {
                <i
                  role="button"
                  class="pi pi-check icon-success tw:cursor-pointer"
                  pTooltip="Copied"></i>
              } @else {
                <i
                  test-id="chat-copy-message-2"
                  class="pi pi-clone tw:text-color tw:cursor-pointer"
                  (click)="copyCode(testInteraction.content, index)"
                  pTooltip="Copy Message"></i>
              }

              <i
                test-id="chat-more-actions-2"
                class="pi pi-ellipsis-v tw:text-color tw:cursor-pointer"
                pTooltip="More Actions"
                tooltipPosition="bottom"
                (click)="userMoreOptionsPopover.toggle($event)"></i>
            </div>

            <p-popover #userMoreOptionsPopover>
              <div class="tw:flex tw:flex-col">
                <!-- Pin / Loading -->
                @if (writeAccess() && $last) {
                  <p-button
                    test-id="chat-pin-message"
                    [buttonProps]="{ autofocus: false }"
                    (click)="pinMessage(testInteraction)"
                    [loading]="
                      pinningMessageInfo().interactionId === testInteraction.id &&
                      pinningMessageInfo().isSubmitting
                    "
                    [disabled]="
                      pinningMessageInfo().interactionId === testInteraction.id &&
                      pinningMessageInfo().isSubmitting
                    "
                    pTooltip="Pin this message for the pipeline"
                    tooltipPosition="bottom"
                    styleClass="tw:justify-start"
                    severity="contrast"
                    variant="text"
                    size="small"
                    fluid
                    icon="pi pi-thumbtack"
                    label="Pin Message" />
                }

                <!-- Copy Link Icon -->
                @if (testRun()?.id) {
                  <p-button
                    test-id="chat-copy-link-2"
                    [buttonProps]="{ autofocus: false }"
                    pTooltip="Copy link to this message"
                    tooltipPosition="bottom"
                    (click)="copyMessageLink(testInteraction.id, $event)"
                    styleClass="tw:justify-start"
                    [severity]="
                      copiedInfo().index === testInteraction.id &&
                      copiedInfo().checked &&
                      copiedInfo().type === 'link'
                        ? 'success'
                        : 'contrast'
                    "
                    size="small"
                    variant="text"
                    fluid
                    [icon]="
                      copiedInfo().index === testInteraction.id &&
                      copiedInfo().checked &&
                      copiedInfo().type === 'link'
                        ? 'pi pi-check'
                        : 'pi pi-link'
                    "
                    label="Copy Link" />
                }

                @if (testInteraction.isAiGenerated && testInteraction.id) {
                  <ng-container
                    *ngTemplateOutlet="
                      interactionFeedbackActionsTemplate;
                      context: {
                        interaction: testInteraction,
                        popover: userMoreOptionsPopover,
                        showCommentAction: writeAccess(),
                      }
                    " />
                }

                <!-- Delete -->
                @if (writeAccess() && !testRun()?.hasEnded) {
                  <p-button
                    test-id="chat-delete"
                    [buttonProps]="{ autofocus: false }"
                    (click)="deleteMessage(testInteraction.id, $event)"
                    styleClass="tw:justify-start"
                    severity="danger"
                    fluid
                    size="small"
                    variant="text"
                    icon="pi pi-trash"
                    label="Delete" />
                }
              </div>
            </p-popover>

            @if (testInteraction.isAiGenerated && testInteraction.id) {
              <ng-container
                *ngTemplateOutlet="
                  interactionCommentSectionTemplate;
                  context: { interaction: testInteraction }
                " />
              <ng-container
                *ngTemplateOutlet="
                  interactionActivityTemplate;
                  context: { interaction: testInteraction }
                " />
            }
          </div>
        }

        <!-- Internal Message -->
        @if (testInteraction.role === CHAT_ROLE.INTERNAL) {
          <p-message
            [motionOptions]="{ disabled: true }"
            severity="warn"
            class="tw:w-fit tw:mx-auto tw:my-2">
            <div class="tw:flex tw:flex-col tw:justify-center tw:w-full tw:gap-2">
              <small class="tw:text-center">
                {{ testInteraction.createdDate | messageTime }}
              </small>
              @let textContent = testInteraction.content.find(x => x.type === 'text').text;
              <div
                class="tw:whitespace-pre-wrap tw:wrap-anywhere"
                [innerHTML]="textContent | linkify"></div>
            </div>
          </p-message>
        }
        <!-- System Message -->
        @if (testInteraction.role === CHAT_ROLE.SYSTEM) {
          <div
            [id]="'message-' + testInteraction.id"
            class="tw:group tw:flex tw:gap-3 tw:w-fit tw:max-w-[80%] tw:my-2"
            [ngClass]="{
              'tw:mb-auto': last && !newMessageLoading(),
              'tw:border tw:border-primary tw:rounded':
                testInteraction.id && highlightedMessageId() === testInteraction.id,
            }">
            <p-avatar
              class="tw:shrink-0 tw:bg-black"
              shape="circle">
              <i class="pi pi-desktop tw:text-surface-0"></i>
            </p-avatar>

            <div class="tw:flex tw:flex-col tw:gap-2">
              <strong>
                System
                <small class="tw:font-normal tw:text-muted-color-emphasis">
                  {{ testInteraction.createdDate | messageTime }}
                </small>
              </strong>
              @let textContent = testInteraction.content.find(x => x.type === 'text').text;
              @if (textContent.length > 500) {
                <p-inplace [ngStyle]="{ '--p-inplace-padding': '0' }">
                  <ng-template #display>
                    <div
                      class="tw:whitespace-pre-wrap tw:wrap-anywhere tw:line-clamp-3"
                      [innerHTML]="textContent | linkify"></div>
                    <p-button
                      test-id="chat-show-more"
                      [buttonProps]="{ autofocus: false }"
                      variant="text"
                      severity="primary"
                      size="small"
                      label="Show more"
                      styleClass="tw:py-0" />
                  </ng-template>
                  <ng-template
                    #content
                    let-closeCallback="closeCallback">
                    <div
                      test-id="chat-click-to-collapse"
                      class="tw:whitespace-pre-wrap tw:wrap-anywhere"
                      pTooltip="Click to collapse"
                      [innerHTML]="textContent | linkify"
                      (click)="closeCallback($event)"></div>
                  </ng-template>
                </p-inplace>
              } @else {
                <div
                  class="tw:whitespace-pre-wrap tw:wrap-anywhere"
                  [innerHTML]="textContent | linkify"></div>
              }
              @if (debugMode()) {
                <div
                  class="tw:flex tw:flex-wrap tw:gap-2"
                  [ngClass]="{
                    'tw:invisible tw:group-hover:visible': !last,
                  }">
                  @if (testInteraction.context) {
                    <p-tag
                      class="tw:gap-2 tw:px-2 tw:items-center tw:font-semibold tw:cursor-pointer tw:bg-purple-500/10 tw:text-purple-500"
                      [ngStyle]="{
                        '--p-tag-icon-size': '0.9rem',
                      }"
                      severity="secondary"
                      icon="pi pi-file-o"
                      value="Context"
                      (click)="
                        updateInteractionState(testInteraction.id, {
                          isContextExpanded: !interactionState().get(testInteraction.id)
                            ?.isContextExpanded,
                        })
                      " />
                  }
                  @if (testInteraction.executionLogs) {
                    <p-tag
                      class="tw:gap-2 tw:px-2 tw:items-center tw:font-semibold tw:cursor-pointer"
                      [ngStyle]="{
                        '--p-tag-icon-size': '0.9rem',
                      }"
                      severity="warn"
                      icon="pi pi-code"
                      value="Logs"
                      (click)="
                        updateInteractionState(testInteraction.id, {
                          isLogsExpanded: !interactionState().get(testInteraction.id)
                            ?.isLogsExpanded,
                        })
                      " />
                  }
                </div>
              }
            </div>
          </div>
        }
        <!-- Event Message -->
        @if (testInteraction.role === CHAT_ROLE.EVENT) {
          <div
            [id]="'message-' + testInteraction.id"
            class="tw:group tw:flex tw:items-start tw:gap-3 tw:w-full tw:max-w-[80%] tw:border-l tw:border-surface tw:p-1 tw:ps-4 tw:ms-[5rem]"
            [ngClass]="{
              'tw:mb-auto': last && !newMessageLoading(),
              'tw:border tw:border-primary tw:rounded':
                testInteraction.id && highlightedMessageId() === testInteraction.id,
            }">
            <small class="tw:text-muted-color tw:text-xs tw:shrink-0 tw:mt-2">
              {{ testInteraction.createdDate | messageTime }}
            </small>
            @let textContent = testInteraction.content.find(x => x.type === 'text').text;
            @if (textContent.length > 100) {
              <p-inplace
                class="tw:min-w-0 tw:flex-1"
                [ngStyle]="{ '--p-inplace-padding': '0' }">
                <ng-template #display>
                  <div class="tw:flex tw:w-full tw:items-center tw:min-w-0 tw:group tw:mt-1">
                    <div
                      class="tw:min-w-0 tw:flex-1 tw:overflow-hidden tw:text-muted-color tw:text-sm tw:font-medium tw:line-clamp-1"
                      [innerHTML]="textContent | linkify"></div>
                    <p-button
                      test-id="chat-icon-angle-right"
                      [buttonProps]="{ autofocus: false }"
                      variant="text"
                      severity="secondary"
                      size="small"
                      icon="pi pi-angle-right"
                      styleClass="tw:py-0 tw:px-0 tw:invisible tw:group-hover:visible tw:shrink-0" />
                  </div>
                </ng-template>
                <ng-template
                  #content
                  let-closeCallback="closeCallback">
                  <div
                    test-id="chat-click-to-collapse-2"
                    pTooltip="Click to collapse"
                    (click)="closeCallback($event)">
                    <ng-container
                      *ngTemplateOutlet="
                        interactionContentTemplate;
                        context: {
                          content: testInteraction.content,
                          contentType: testInteraction.contentType,
                          index: testInteraction.id || index,
                        }
                      " />
                  </div>
                </ng-template>
              </p-inplace>
            } @else {
              <ng-container
                *ngTemplateOutlet="
                  interactionContentTemplate;
                  context: {
                    content: testInteraction.content,
                    contentType: testInteraction.contentType,
                    index: testInteraction.id || index,
                  }
                " />
            }
          </div>
        }
      }
      <ng-container *ngTemplateOutlet="autoChatPromptTemplate()" />
    </div>
  }
  <!-- Anchor to keep the content scrolled to bottom -->
  <div
    #chatBottomAnchor
    class="tw:pt-1"></div>
  <!-- Sticky footer: input area and scroll to bottom button is sticky to bottom -->
  <div
    class="tw:sticky tw:bottom-0 tw:w-full tw:flex tw:flex-col tw:gap-2 tw:z-9"
    [ngClass]="{
      'tw:relative tw:mb-auto': dedupedInteractions()?.length === 0 && writeAccess(),
    }">
    @if (showScrollButton()) {
      <div class="tw:w-fit tw:mx-auto">
        <p-button
          test-id="chat-scroll-to-bottom"
          [buttonProps]="{ autofocus: false }"
          rounded
          icon="pi pi-arrow-down"
          size="small"
          severity="contrast"
          (click)="scrollToBottom()" />
      </div>
    }
    @if (pipeline() && (testRun()?.hasEnded || !isAutoChat())) {
      <div
        class="tw:px-4 tw:pb-4 tw:bg-(--p-content-background)"
        [ngClass]="{
          'tw:mb-auto': dedupedInteractions()?.length === 0 && writeAccess(),
        }">
        <div
          class="tw:relative tw:rounded-2xl tw:flex tw:flex-col tw:border tw:border-surface tw:shadow tw:p-2 tw:flex-1">
          @if (testRun()?.hasEnded) {
            <div class="tw:text-center tw:text-muted-color tw:p-2">
              This session has ended. Chat is disabled.
              @if (testSessionId()) {
                <p-button
                  [buttonProps]="{ autofocus: false }"
                  label="Review Session"
                  variant="text"
                  size="small"
                  styleClass="tw:ms-auto"
                  (click)="reviewSession.emit()"
                  test-id="end-session-button" />
              }
            </div>
          } @else {
            <ng-container *ngTemplateOutlet="personaSelectionTemplate()" />
            <div
              class="message-editor col"
              [ngClass]="{
                'tw:mt-3': personaSelectionTemplate(),
              }">
              <input
                test-id="chat-input-file"
                #composerFileInput
                type="file"
                accept="image/*"
                class="tw:hidden"
                multiple
                (change)="composer.onFileSelect($event)" />
              @if (composer.attachments().length > 0 || composer.isUploading()) {
                <div class="tw:flex tw:flex-wrap tw:gap-2 tw:p-1">
                  @for (attachment of composer.attachments(); track attachment.id) {
                    <div
                      class="tw:relative tw:h-20 tw:w-20 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-surface tw:bg-emphasis">
                      @if (attachment.type?.startsWith('image/')) {
                        <img
                          class="tw:h-full tw:w-full tw:cursor-zoom-in tw:object-cover"
                          [src]="attachment.url"
                          [alt]="attachment.name"
                          (click)="imagePreview.open(attachment.url)" />
                      } @else {
                        <div
                          class="tw:flex tw:h-full tw:w-full tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:p-2 tw:text-center">
                          <i class="pi pi-file tw:text-lg tw:text-muted-color"></i>
                          <span class="tw:w-full tw:truncate tw:text-xs tw:text-muted-color">
                            {{ attachment.name }}
                          </span>
                        </div>
                      }

                      <p-button
                        test-id="chat-remove-file"
                        [buttonProps]="{ autofocus: false }"
                        class="tw:absolute tw:top-0 tw:right-0 tw:m-1"
                        [style]="{
                          '--p-button-sm-icon-only-width': '1.3rem',
                        }"
                        rounded
                        severity="contrast"
                        size="small"
                        icon="pi pi-times tw:text-xs"
                        (click)="composer.removeFile(attachment)" />
                    </div>
                  }
                  @if (composer.isUploading()) {
                    <div
                      class="tw:flex tw:h-20 tw:w-20 tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:rounded-2xl tw:border tw:border-dashed tw:border-surface tw:bg-emphasis tw:text-muted-color">
                      <i class="pi pi-spinner pi-spin"></i>
                      <small>Uploading</small>
                    </div>
                  }
                </div>
              }
              @if (writeAccess()) {
                <textarea
                  test-id="message-input"
                  [pAutoFocus]="true"
                  class="tw:border-none tw:outline-0 tw:shadow-none tw:max-h-40 tw:[field-sizing:content]"
                  rows="1"
                  fluid
                  placeholder="Write your message here..."
                  (keydown.enter)="
                    !newMessageLoading() && !composer.isUploading() && emitPrompt($event)
                  "
                  (paste)="composer.onPaste($event)"
                  [(ngModel)]="composer.userMessage"
                  valueChangeEvent="keyup change"
                  pTextarea></textarea>
              } @else {
                <div class="tw:text-muted-color tw:p-2">
                  @if (testSessionId()) {
                    You are not authorized to proceed with the test session.
                  } @else {
                    You are not authorized to create a test session.
                  }
                </div>
              }
            </div>
            <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
              @if (writeAccess()) {
                <p-button
                  test-id="chat-upload-images"
                  [buttonProps]="{ autofocus: false }"
                  icon="pi pi-paperclip"
                  severity="secondary"
                  variant="text"
                  rounded
                  pTooltip="Upload images"
                  tooltipPosition="top"
                  [disabled]="composer.isUploading()"
                  (click)="composerFileInput.click()" />

                <div class="tw:flex tw:items-center tw:gap-2 tw:ms-auto">
                  <p-button
                    test-id="chat-voice"
                    #voiceBtn
                    [buttonProps]="{ autofocus: false }"
                    [ngClass]="{
                      'btn-hover-scale': !voiceChat.isRecording() && !voiceChat.isLoading(),
                    }"
                    [pTooltip]="
                      voiceChat.isRecording()
                        ? 'Stop dictation to transcribe the message'
                        : 'Start dictation and transcribe the user message'
                    "
                    position="left"
                    (click)="voiceChat.isRecording() ? stopRecording() : startRecording(voiceBtn)"
                    variant="outlined"
                    severity="secondary"
                    [label]="
                      voiceChat.isLoading()
                        ? 'Loading...'
                        : voiceChat.isRecording()
                          ? 'Listening'
                          : 'Voice'
                    ">
                    <i
                      class="pi"
                      [ngClass]="
                        voiceChat.isLoading()
                          ? 'pi-spinner pi-spin tw:text-color tw:text-xl'
                          : voiceChat.isRecording()
                            ? 'pi-pause-circle icon-danger'
                            : 'pi-microphone'
                      "></i>
                  </p-button>

                  <p-button
                    test-id="chat-emit-prompt"
                    [buttonProps]="{ autofocus: false }"
                    icon="pi pi-arrow-up"
                    [disabled]="
                      (!composer.userMessage() && composer.attachments().length === 0) ||
                      newMessageLoading() ||
                      composer.isUploading()
                    "
                    (click)="emitPrompt()" />
                </div>
              }
            </div>
          }
        </div>
      </div>
    }
  </div>
</div>

<!-- START: feedback actions template -->
<ng-template
  #interactionFeedbackActionsTemplate
  let-interaction="interaction"
  let-popover="popover"
  let-showCommentAction="showCommentAction">
  @if (showCommentAction) {
    <p-button
      test-id="chat-add-comment"
      [buttonProps]="{ autofocus: false }"
      (click)="
        updateInteractionState(interaction.id, {
          isAddCommentVisible: true,
        });
        popover.hide()
      "
      styleClass="tw:justify-start"
      severity="contrast"
      fluid
      size="small"
      variant="text"
      icon="pi pi-plus"
      label="Add Comment" />
  }
  <p-button
    test-id="chat-view-feedback-activity"
    [buttonProps]="{ autofocus: false }"
    (click)="
      updateInteractionState(interaction.id, {
        visibleActivityCount: 5,
      });
      popover.hide()
    "
    styleClass="tw:justify-start"
    severity="contrast"
    fluid
    size="small"
    variant="text"
    icon="pi pi-list"
    label="View Feedback Activity" />
</ng-template>
<!-- END: feedback actions template -->

<!-- START: Comment section template -->
<ng-template
  #interactionCommentSectionTemplate
  let-interaction="interaction">
  @if (interactionState().get(interaction.id)?.isAddCommentVisible) {
    @if (interaction.rating == FEEDBACK_TYPE.DISLIKE) {
      <p-message
        [motionOptions]="{ disabled: true }"
        size="small"
        icon="pi pi-info-circle"
        severity="info">
        Explain what went wrong and the reasoning behind it to help improve the AI assistant.
      </p-message>
    }
    <div
      class="tw:flex tw:flex-col tw:gap-1 tw:border-2 tw:border-surface tw:rounded-xl tw:p-2 tw:shadow">
      <p-selectButton
        test-id="chat-p-selectbutton"
        #issueType
        styleClass="issue-type"
        [ngStyle]="{
          '--p-togglebutton-content-sm-padding': '0.1rem 0.5rem',
          '--p-togglebutton-sm-font-size': '0.8rem',
        }"
        size="small"
        [disabled]="!writeAccess()"
        [ngModel]="interaction.issueType || ISSUE_TYPE.OTHER"
        [options]="FEEDBACK_ISSUE_TYPES"
        [autofocus]="true" />
      <textarea
        test-id="chat-add-a-comment"
        #commentTextArea
        [disabled]="!writeAccess()"
        class="tw:border-none tw:outline-0 tw:shadow-none"
        rows="1"
        [fluid]="true"
        [pAutoFocus]="true"
        [placeholder]="
          interaction.rating == FEEDBACK_TYPE.DISLIKE
            ? 'Add more details about what went wrong...'
            : 'Add a comment...'
        "
        pTextarea
        (keydown.arrowUp)="$event.stopPropagation()"
        (keydown.arrowDown)="$event.stopPropagation()"></textarea>
      <div class="tw:flex tw:gap-2 tw:justify-end">
        <p-button
          test-id="chat-save-comment"
          [buttonProps]="{ autofocus: false }"
          size="small"
          label="Save Comment"
          [loading]="interactionState().get(interaction.id)?.isCommentSaving"
          (click)="
            postCommentFeedback(interaction.id, commentTextArea.value, issueType.value);
            commentTextArea.value = ''
          " />
      </div>
    </div>
  }
</ng-template>
<!-- END: Comment section template -->

<!-- START: Activity section template -->
<ng-template
  #interactionActivityTemplate
  let-interaction="interaction">
  @if ((interactionState().get(interaction.id)?.visibleActivityCount || 0) > 0) {
    <div
      class="tw:flex tw:flex-col tw:p-3 tw:border tw:border-surface tw:rounded-2xl tw:relative tw:max-w-[50rem]">
      <div class="tw:font-bold tw:text-muted-color">Activity</div>
      <i
        test-id="chat-close-activity"
        class="pi pi-times tw:absolute tw:top-0 tw:end-0 tw:m-3 tw:cursor-pointer"
        title="Close Activity"
        (click)="updateInteractionState(interaction.id, { visibleActivityCount: 0 })"></i>
      <div class="tw:flex tw:flex-col tw:py-2 tw:gap-3">
        @if ((interaction.feedbackHistory || []).length === 0) {
          <div class="tw:text-muted-color">No activity yet.</div>
        }
        @for (
          historyItem of (interaction.feedbackHistory || [])
            .slice()
            .reverse()
            .slice(0, interactionState().get(interaction.id)?.visibleActivityCount);
          track historyItem.createdDate
        ) {
          <div class="tw:flex tw:gap-2">
            <p-avatar
              class="tw:shrink-0 tw:mt-1"
              [ngStyle]="{
                '--p-avatar-width': '1.75rem',
                '--p-avatar-height': '1.75rem',
                '--p-avatar-font-size': '0.75rem',
                backgroundColor: (historyItem.createdBy || '' | uniqueRgbColor),
                color: 'white',
              }"
              [label]="historyItem.createdBy || '' | nameBadge"
              shape="circle" />
            <div class="tw:flex tw:flex-col">
              <div>
                <strong>
                  {{ historyItem.createdBy }}
                </strong>
                <small class="tw:text-muted-color tw:px-1">
                  {{ historyItem.createdDate | messageTime }}
                </small>
              </div>
              @if (historyItem.message) {
                <small
                  class="tw:break-normal"
                  [innerText]="historyItem.message"></small>
              }
              @if (historyItem.comment || historyItem.issueType) {
                <small class="tw:break-normal">
                  @if (historyItem.issueType === ISSUE_TYPE.OTHER) {
                    {{ historyItem.comment }}
                  } @else {
                    [{{ historyItem.issueType }}] {{ historyItem.comment }}
                  }
                </small>
              }
            </div>
          </div>
        }
        <!-- Show More -->
        @if (
          (interaction.feedbackHistory || []).length >
          interactionState().get(interaction.id)?.visibleActivityCount
        ) {
          <p-button
            test-id="chat-show-more-activity"
            [buttonProps]="{ autofocus: false }"
            styleClass="tw:p-0"
            size="small"
            variant="text"
            label="Show More Activity"
            (click)="
              updateInteractionState(interaction.id, {
                visibleActivityCount:
                  interactionState().get(interaction.id)?.visibleActivityCount + 5,
              })
            " />
        }
      </div>
    </div>
  }
</ng-template>
<!-- END: Activity section template -->

@if (integrationsPanel.showDialog()) {
  <!-- FIXME: This is a hardcoded ID - could be an issue  -->
  <cor-platform-settings-integrations
    [id]="1"
    [showProviderConnectionDialog]="{
      providerType: PROVIDER_TYPES.VOICE_PROVIDERS,
      providerName: 'deepgram',
    }"
    (onHideDialog)="hideIntegrationProvider()" />
}

<!-- Tool call popover -->
<p-popover #toolCallPopoverElement>
  <div class="tw:px-3 tw:pb-2 tw:flex tw:flex-col tw:overflow-auto tw:max-h-[50vh] tw:max-w-[50vw]">
    <div class="tw:font-semibold tw:text-color tw:flex tw:gap-2 tw:items-center tw:py-2">
      <i class="pi pi-wrench"></i>
      {{ toolCallPopover.details()?.request?.function?.name }}
    </div>
    <div class="tw:border-t tw:border-surface tw:h-[0.1em]"></div>
    <!-- Arguments -->
    <div
      class="tw:font-semibold tw:text-muted-color-emphasis tw:sticky tw:top-0 tw:py-1 tw:bg-(--p-content-background)">
      Arguments
    </div>
    @if (toolCallPopover.details()?.request?.function?.arguments) {
      <pre
        class="tw:p-2 tw:rounded-sm tw:bg-emphasis tw:wrap-anywhere tw:whitespace-pre-wrap"
        [innerHTML]="toolCallPopover.details().request.function.arguments | cellPrettify"></pre>
    } @else {
      -
    }
    <!-- Output -->
    <div
      class="tw:font-semibold tw:text-muted-color-emphasis tw:sticky tw:top-0 tw:py-1 tw:bg-(--p-content-background)">
      Output
    </div>
    @if (toolCallPopover.details()?.response?.content) {
      <pre
        class="tw:p-2 tw:rounded-sm tw:bg-emphasis tw:wrap-anywhere tw:whitespace-pre-wrap"
        [innerHTML]="toolCallPopover.details().response.content | cellPrettify"></pre>
    } @else {
      -
    }
  </div>
</p-popover>

<!-- Attachment Image preview -->
@if (imagePreview.isVisible()) {
  <p-dialog
    [visible]="imagePreview.isVisible()"
    [modal]="true"
    [dismissableMask]="true"
    [draggable]="false"
    [resizable]="false"
    [closable]="false"
    appendTo="body"
    [showHeader]="false"
    maskStyleClass="tw:backdrop-blur-sm"
    styleClass="tw:!m-0 tw:!h-dvh tw:!w-screen tw:!max-h-dvh tw:!max-w-none tw:!border-0 tw:!bg-black/40 tw:!shadow-none"
    contentStyleClass="tw:!h-full tw:!overflow-hidden tw:!bg-transparent tw:!p-0">
    <ng-template #headless>
      <div class="tw:relative tw:flex tw:h-full tw:w-full tw:items-center tw:justify-center tw:p-4">
        <p-button
          test-id="chat-close"
          [buttonProps]="{ autofocus: false }"
          class="tw:absolute tw:right-4 tw:top-4 tw:z-10"
          rounded
          severity="contrast"
          size="small"
          icon="pi pi-times tw:text-xs"
          (click)="imagePreview.close()" />

        <img
          class="tw:block tw:h-auto tw:max-h-full tw:max-w-full tw:rounded-xl tw:object-contain"
          [src]="imagePreview.url()"
          alt="Image preview" />
      </div>
    </ng-template>
  </p-dialog>
}
